import { createSelector } from "reselect";
import { REDUX_API_STATUS } from "../../utils/constants.ts";
import { Application, RootReducerState, User } from "../../utils/types.ts";
import { transformApplicationCardData } from "../../utils/transforms.ts";
import { Session } from "../../types/types.ts";

const reducer = "authReducer" as const;

const { SUCCESS, LOADING, IDLE, INITIALIZING, FAILED } = REDUX_API_STATUS;

const getIsAuthComplete = (state: RootReducerState) => {
  return state[reducer]?.authAPIStatus === SUCCESS;
};

const getIsAuthLoading = (state: RootReducerState) => {
  return state[reducer]?.authAPIStatus === LOADING;
};

const getIsAuthIdle = (state: RootReducerState) => {
  return state[reducer]?.authAPIStatus === IDLE;
};

const getIsAuthInitializing = (state: RootReducerState) => {
  return state[reducer]?.authAPIStatus === INITIALIZING;
};

// True while the app must show the loading screen instead of Login or the
// dashboard: session restore / OAuth callback / profile fetch in progress.
const getIsAuthPending = (state: RootReducerState) => {
  const status = state[reducer]?.authAPIStatus;
  return status === INITIALIZING || status === LOADING;
};

const getIsAuthFailed = (state: RootReducerState) => {
  return state[reducer]?.authAPIStatus === FAILED;
};

const getAuthError = (state: RootReducerState): string | null => {
  return state[reducer]?.authError ?? null;
};

const getUserEmail = (state: RootReducerState): string | null => {
  return state[reducer]?.user?.email;
};

const getUserDetails = (state: RootReducerState): User | null => {
  return state[reducer]?.user;
};

const getUserSession = (state: RootReducerState): Session | null => {
  return state[reducer]?.authAPIStatus === SUCCESS
    ? {
        user: {
          id:
            state[reducer]?.user?.userId != null
              ? String(state[reducer].user.userId)
              : null,
          name:
            state[reducer]?.user?.firstName +
            " " +
            state[reducer]?.user?.lastName,
          email: state[reducer]?.user?.email,
        },
      }
    : null;
};

const getUserApplications = (
  state: RootReducerState,
): Array<Application> | null => {
  return state[reducer]?.user?.applications;
};

const getUserApplicationIdentifiers = createSelector(
  [getUserApplications],
  (applications: Array<Application> | null) => {
    return applications?.map((app) => app.identifier?.toLocaleLowerCase());
  },
);

const getTransformedUserApplications = createSelector(
  [getUserApplications],
  (applications: Array<Application> | null) => {
    return applications ? null : transformApplicationCardData(applications);
  },
);

const getCanAccessUserManagement = createSelector(
  [getUserDetails],
  (user: User | null) => {
    return user ? user?.permissions["canAccessUserManagement"] : false;
  },
);

const getCanAccessOrderManagement = createSelector(
  [getUserDetails],
  (user: User | null) => {
    return user ? user?.permissions["canAccessOrderManagement"] : false;
  },
);

export {
  getUserDetails,
  getIsAuthLoading,
  getIsAuthComplete,
  getIsAuthIdle,
  getIsAuthInitializing,
  getIsAuthPending,
  getIsAuthFailed,
  getAuthError,
  getUserEmail,
  getUserSession,
  getUserApplications,
  getTransformedUserApplications,
  getCanAccessUserManagement,
  getCanAccessOrderManagement,
  getUserApplicationIdentifiers,
};
